<script lang="ts" setup>
import { ref, computed, toRef } from "vue";
import {
  useProductVersions,
  useCreateProductVersion,
  usePublishProductVersion,
  usePublishProductVersionAsPreview,
  useEndPreview,
  useArchiveProductVersion,
  useDeleteProductVersion,
} from "@/queries/products";
import { useProjectWorkflows } from "@/queries/projects";
import { toVersionDetails } from "@/types/version-types";
import CreateVersionModal from "@/views/shared/components/modals/CreateVersionModal.vue";
import VersionDetailsModal from "@/views/shared/components/modals/VersionDetailsModal.vue";
import PublishVersionModal from "@/views/shared/components/modals/PublishVersionModal.vue";

interface Props {
  productId: string;
  masterProjectId?: string;
  entityType: "project" | "product";
  isPublisherMasterProject?: boolean;
}

const props = defineProps<Props>();

const productIdRef = toRef(props, "productId");
const masterProjectIdRef = computed(() => props.masterProjectId ?? "");
const workflowsEnabled = computed(() => !!masterProjectIdRef.value);

const showVersionsTab = computed(() => {
  if (props.entityType === "product") return true;
  return props.isPublisherMasterProject;
});

// Queries
const { versions: apiVersions, versionsLoading } = useProductVersions(productIdRef);
const { projectWorkflows: workflows } = useProjectWorkflows(masterProjectIdRef, workflowsEnabled);

// Mutations
const { createVersionAsync, createVersionPending } = useCreateProductVersion(productIdRef);
const { publishVersionAsync, publishVersionPending } = usePublishProductVersion(productIdRef);
const { publishAsPreviewAsync, publishAsPreviewPending } = usePublishProductVersionAsPreview(productIdRef);
const { endPreviewAsync, endPreviewPending } = useEndPreview(productIdRef);
const { archiveVersionAsync } = useArchiveProductVersion(productIdRef);
const { deleteVersionAsync } = useDeleteProductVersion(productIdRef);

// Map API versions to UI VersionDetails for modal compatibility
const versions = computed(() =>
  apiVersions.value.map((v) => toVersionDetails(v, workflows.value)),
);

// The single live version (at most one per product). Null until the first publish.
const publishedVersion = computed(() =>
  apiVersions.value.find((v) => v.status === "Published"),
);

const publishedVersionNumber = computed(() => publishedVersion.value?.versionNumber ?? "—");

// Latest version by creation date (first in the list since API sorts desc) — used for suggesting next version number
const latestVersionNumber = computed(() => apiVersions.value[0]?.versionNumber ?? publishedVersionNumber.value);

// Hide archived versions by default — they pile up over time and clutter the active list.
const showArchived = ref(false);
const archivedCount = computed(() => versions.value.filter((v) => v.status === "Archived").length);
const visibleVersions = computed(() =>
  showArchived.value
    ? versions.value
    : versions.value.filter((v) => v.status !== "Archived"),
);

// A product version snapshots workflow versions, so creating one is only meaningful once at least
// one workflow in the master project has a published version (the create modal filters out drafts).
const hasPublishableWorkflow = computed(() =>
  workflows.value.some((w) => w.version && w.version !== "Draft"),
);
const createDisabledReason = computed(() =>
  hasPublishableWorkflow.value
    ? ""
    : "Publish at least one workflow in the master project before creating a product version.",
);

// Modal state
const showCreateModal = ref(false);
const showDetailsModal = ref(false);
const showPublishModal = ref(false);
const showDeleteConfirmId = ref<string | null>(null);
const showArchiveConfirmId = ref<string | null>(null);
const showEndPreviewConfirmId = ref<string | null>(null);
const selectedVersion = ref<any>(null);

const subscriberCountByVersionId = computed(() => {
  const map = new Map<string, number>();
  for (const v of apiVersions.value) {
    map.set(v.id, v.subscriberCount ?? 0);
  }
  return map;
});

async function confirmEndPreview() {
  if (!showEndPreviewConfirmId.value) return;
  await endPreviewAsync(showEndPreviewConfirmId.value);
  showEndPreviewConfirmId.value = null;
}

function getStatusClass(status: string): string {
  switch (status.toLowerCase()) {
    case "published":
      return "bg-primary";
    case "preview":
      return "bg-info";
    case "draft":
      return "bg-warning";
    case "archived":
      return "bg-secondary";
    default:
      return "bg-warning";
  }
}

function versionDateLabel(v: { status: string }): string {
  switch (v.status) {
    case "Draft": return "Created";
    case "Archived": return "Archived";
    default: return "Published";
  }
}

function versionDateValue(v: { status: string; createdDate: string; publishedDate: string; archivedDate?: string }): string {
  switch (v.status) {
    case "Draft": return v.createdDate;
    case "Archived": return v.archivedDate ?? v.publishedDate;
    default: return v.publishedDate;
  }
}

function formatDate(dateString: string): string {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function createNewVersion() {
  showCreateModal.value = true;
}

function viewVersion(versionId: string) {
  const version = versions.value.find((v) => v.id === versionId);
  if (version) {
    selectedVersion.value = version;
    showDetailsModal.value = true;
  }
}

function publishVersion(versionId: string) {
  const version = versions.value.find((v) => v.id === versionId);
  if (version) {
    const deletedWorkflowNames = (version.workflowVersions ?? [])
      .filter((w) => w.isDeleted)
      .map((w) => w.name);
    selectedVersion.value = { ...version, deletedWorkflowNames };
    showPublishModal.value = true;
  }
}

function archiveVersion(versionId: string) {
  // Preview can't go through the generic archive path — the domain refuses, and the side effect
  // (revert preview-subscribers to Published) lives in the EndPreview command.
  const v = apiVersions.value.find((x) => x.id === versionId);
  if (v?.status === "Preview") {
    showEndPreviewConfirmId.value = versionId;
  } else {
    showArchiveConfirmId.value = versionId;
  }
}

async function confirmArchive() {
  if (!showArchiveConfirmId.value) return;
  await archiveVersionAsync(showArchiveConfirmId.value);
  showArchiveConfirmId.value = null;
  showDetailsModal.value = false;
  selectedVersion.value = null;
}

async function deleteVersion(versionId: string) {
  await deleteVersionAsync(versionId);
  showDetailsModal.value = false;
  selectedVersion.value = null;
}

async function handleCreateVersion(versionData: any) {
  await createVersionAsync({
    versionNumber: versionData.version,
    description: versionData.description,
    releaseNotes: versionData.releaseNotes,
    selectedWorkflowIds: versionData.selectedWorkflowIds,
  });
  showCreateModal.value = false;
}

async function handlePublishVersion(_payload: { versionId: string }) {
  if (!selectedVersion.value) return;
  await publishVersionAsync(selectedVersion.value.id);
  showPublishModal.value = false;
  showDetailsModal.value = false;
  selectedVersion.value = null;
}

async function handlePublishPreview(payload: { versionId: string; subscriberOrgIds: string[] }) {
  await publishAsPreviewAsync(payload);
  showPublishModal.value = false;
  showDetailsModal.value = false;
  selectedVersion.value = null;
}
</script>

<template>
  <div v-if="!showVersionsTab" class="text-center py-5">
    <i class="bi bi-info-circle display-1 text-muted"></i>
    <h3 class="mt-3 text-muted">Versions Not Available</h3>
    <p class="text-muted">
      Version management is only available for Publisher Master projects and
      products.
    </p>
  </div>

  <div v-else class="versions-tab">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2 class="card-title mb-1">
          Product Versions
        </h2>
      </div>
      <div class="d-flex gap-2">
        <span :title="createDisabledReason" :class="{ 'd-inline-block': !hasPublishableWorkflow }">
          <button
            class="bb-btn-primary-cta"
            @click="createNewVersion"
            :disabled="createVersionPending || !hasPublishableWorkflow"
          >
            <i class="bi bi-plus"></i>
            <span class="d-none d-sm-inline">Create New Version</span
            ><span class="d-sm-none">New Version</span>
          </button>
        </span>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="versionsLoading" class="text-center py-5 text-muted">
      <div class="spinner-border spinner-border-sm me-2"></div>
      Loading versions…
    </div>

    <!-- Empty state -->
    <div v-else-if="!versions.length" class="text-center py-5 text-muted">
      <i class="bi bi-tag display-5"></i>
      <p class="mt-3">No versions yet.</p>
      <p v-if="hasPublishableWorkflow" class="small">
        Create your first version to snapshot the current workflow state.
      </p>
      <p v-else class="small">
        Publish at least one workflow in the master project, then return here to create your first version.
      </p>
    </div>

    <template v-else>
      <!-- Versions List -->
      <div class="versions-list">
        <div
          v-for="version in visibleVersions"
          :key="version.id"
          class="version-card"
          :class="{ 'active-version': version.isActive }"
        >
          <div class="d-flex justify-content-between align-items-start">
            <div
              class="version-info flex-grow-1"
              @click="viewVersion(version.id)"
            >
              <div class="d-flex align-items-center gap-3 mb-2">
                <h3 class="mb-0">Version {{ version.version }}</h3>
                <span class="badge" :class="getStatusClass(version.status)">
                  {{ version.status }}
                </span>
              </div>
              <p class="text-muted mb-2">{{ version.description }}</p>
              <div class="version-meta">
                <span class="text-muted">
                  <i class="bi bi-calendar"></i>
                  {{ versionDateLabel(version) }}
                  {{ formatDate(versionDateValue(version)) }}
                </span>
                <span v-if="version.workflowVersions?.length" class="text-muted ms-3">
                  <i class="bi bi-diagram-3"></i>
                  {{ version.workflowVersions.length }} workflows
                </span>
                <span
                  v-if="version.status === 'Preview' && subscriberCountByVersionId.get(version.id)"
                  class="text-muted ms-3"
                >
                  <i class="bi bi-people"></i>
                  {{ subscriberCountByVersionId.get(version.id) }}
                  preview {{ subscriberCountByVersionId.get(version.id) === 1 ? 'subscriber' : 'subscribers' }}
                </span>
                <span
                  v-else-if="version.status === 'Published' && subscriberCountByVersionId.get(version.id)"
                  class="text-muted ms-3"
                >
                  <i class="bi bi-people"></i>
                  {{ subscriberCountByVersionId.get(version.id) }}
                  {{ subscriberCountByVersionId.get(version.id) === 1 ? 'subscriber' : 'subscribers' }}
                </span>
              </div>
            </div>

            <div
              class="version-actions"
              v-if="version.status !== 'Published'"
            >
              <div class="d-flex flex-column flex-sm-row gap-2">
                <!-- Draft: Publish + Delete -->
                <template v-if="version.status === 'Draft'">
                  <button
                    class="bb-btn-primary-cta btn-sm"
                    :disabled="publishVersionPending"
                    @click.stop="publishVersion(version.id)"
                  >
                    <i class="bi bi-rocket"></i> Publish
                  </button>
                  <button
                    class="bb-btn-danger btn-sm"
                    @click.stop="showDeleteConfirmId = version.id"
                  >
                    <i class="bi bi-trash"></i> Delete
                  </button>
                </template>

                <!-- Archived: republish (becomes the live version; current Published auto-archives) -->
                <template v-else-if="version.status === 'Archived'">
                  <button
                    class="bb-btn-primary-cta btn-sm"
                    :disabled="publishVersionPending"
                    @click.stop="publishVersion(version.id)"
                  >
                    <i class="bi bi-rocket"></i> Publish
                  </button>
                </template>

                <!-- Preview: promote to Published + Archive (which ends the preview) -->
                <template v-else-if="version.status === 'Preview'">
                  <button
                    class="bb-btn-primary-cta btn-sm"
                    :disabled="publishVersionPending"
                    @click.stop="publishVersion(version.id)"
                    title="Make this the live version — all subscribers move onto it"
                  >
                    <i class="bi bi-rocket"></i> Publish
                  </button>
                  <button
                    class="bb-btn-danger btn-sm"
                    :disabled="endPreviewPending"
                    @click.stop="showEndPreviewConfirmId = version.id"
                    title="Archive this preview — preview subscribers revert to Published"
                  >
                    <i class="bi bi-archive me-1"></i> Archive
                  </button>
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- Show / hide archived toggle -->
        <button
          v-if="archivedCount > 0"
          type="button"
          class="btn btn-link btn-sm align-self-start text-decoration-none"
          @click="showArchived = !showArchived"
        >
          <i :class="showArchived ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
          {{ showArchived ? "Hide" : "Show" }} archived ({{ archivedCount }})
        </button>
      </div>
    </template>

    <!-- Modals -->
    <CreateVersionModal
      :show="showCreateModal"
      :current-version="latestVersionNumber"
      :product-id="productId"
      :workflows="workflows"
      :is-loading="createVersionPending"
      @close="showCreateModal = false"
      @create="handleCreateVersion"
    />

    <VersionDetailsModal
      :show="showDetailsModal"
      :version="selectedVersion"
      :product-id="productId"
      @close="showDetailsModal = false; selectedVersion = null"
      @publish="publishVersion"
      @archive="archiveVersion"
    />

    <PublishVersionModal
      :show="showPublishModal"
      :version="selectedVersion"
      :product-id="productId"
      :current-version-number="publishedVersionNumber"
      @close="showPublishModal = false"
      @publish="handlePublishVersion"
      @publish-preview="handlePublishPreview"
    />

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteConfirmId"
      class="modal fade show d-block"
      tabindex="-1"
      @click.self="showDeleteConfirmId = null"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title text-danger">
              <i class="bi bi-exclamation-triangle me-2"></i>Delete Version
            </h2>
            <button type="button" class="btn-close" @click="showDeleteConfirmId = null"></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to delete version <strong>{{ versions.find(v => v.id === showDeleteConfirmId)?.version }}</strong>?</p>
            <p class="text-muted mb-0">This action cannot be undone.</p>
          </div>
          <div class="modal-footer">
            <button class="bb-btn-cancel" @click="showDeleteConfirmId = null">Cancel</button>
            <button class="btn btn-danger" @click="() => { deleteVersion(showDeleteConfirmId!); showDeleteConfirmId = null; }">Delete</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showDeleteConfirmId" class="modal-backdrop fade show" @click="showDeleteConfirmId = null"></div>

    <!-- Archive Confirmation Modal -->
    <div
      v-if="showArchiveConfirmId"
      class="modal fade show d-block"
      tabindex="-1"
      @click.self="showArchiveConfirmId = null"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title text-warning">
              <i class="bi bi-archive me-2"></i>Archive Version
            </h2>
            <button type="button" class="btn-close" @click="showArchiveConfirmId = null"></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to archive version <strong>{{ versions.find(v => v.id === showArchiveConfirmId)?.version }}</strong>?</p>
            <p class="text-muted mb-0">Archived versions are no longer available to subscribers.</p>
          </div>
          <div class="modal-footer">
            <button class="bb-btn-cancel" @click="showArchiveConfirmId = null">Cancel</button>
            <button class="btn btn-warning" @click="confirmArchive">Archive</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showArchiveConfirmId" class="modal-backdrop fade show" @click="showArchiveConfirmId = null"></div>

    <!-- Archive Preview Confirmation Modal -->
    <div
      v-if="showEndPreviewConfirmId"
      class="modal fade show d-block"
      tabindex="-1"
      @click.self="showEndPreviewConfirmId = null"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title text-warning">
              <i class="bi bi-archive me-2"></i>Archive Preview
            </h2>
            <button type="button" class="btn-close" @click="showEndPreviewConfirmId = null"></button>
          </div>
          <div class="modal-body">
            <p>Archive Preview <strong>{{ versions.find(v => v.id === showEndPreviewConfirmId)?.version }}</strong>?</p>
            <p class="text-muted mb-0">
              Subscribers on this Preview will revert to the current Published version
              (<strong>{{ publishedVersionNumber }}</strong>). Customizations are preserved.
            </p>
          </div>
          <div class="modal-footer">
            <button class="bb-btn-cancel" @click="showEndPreviewConfirmId = null" :disabled="endPreviewPending">Cancel</button>
            <button class="btn btn-warning" @click="confirmEndPreview" :disabled="endPreviewPending">
              <span v-if="endPreviewPending" class="spinner-border spinner-border-sm me-2"></span>
              Archive
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showEndPreviewConfirmId" class="modal-backdrop fade show" @click="showEndPreviewConfirmId = null"></div>
  </div>
</template>

<style scoped lang="scss">
.versions-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.version-card {
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 0;
  padding: 20px;
  transition: all 0.15s ease-in-out;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.version-card:hover {
  border-color: var(--bs-primary);
  box-shadow: 0 4px 12px rgba(var(--bs-primary-rgb), 0.15);
  transform: translateY(-2px);
  z-index: 10;
  position: relative;
}

.active-version {
  border-color: var(--bs-primary);
  background: var(--bs-primary-bg-subtle);
}

[data-bs-theme="dark"] .version-card {
  background: var(--bs-dark);
  border-color: var(--bs-border-color);
}

[data-bs-theme="dark"] .version-card:hover {
  box-shadow: 0 2px 4px rgba(255, 255, 255, 0.1);
}

[data-bs-theme="dark"] .active-version {
  background: var(--bs-primary-bg-subtle);
}

.version-info {
  cursor: pointer;
}

.version-info h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--bs-body-color);
}

.version-meta {
  font-size: 14px;
  color: var(--bs-secondary-color);
}

.version-actions .btn {
  min-width: 80px;
  padding: 0.25rem 0.5rem;
  white-space: nowrap;
}

@media (max-width: 575.98px) {
  .version-actions .btn {
    min-width: 90px;
    padding: 0.25rem 0.5rem;
    white-space: nowrap;
  }
}

.badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
}

@media (max-width: 768px) {
  .version-card {
    padding: 16px;
  }

  .version-info h3 {
    font-size: 16px;
  }

  .d-flex.gap-3 {
    flex-wrap: wrap;
    gap: 8px;
  }
}
</style>
